import React, { useMemo, useState } from 'react';
import { Currency, formatMoney } from '../../../playground/common/format';
import { RequestResponseViewer } from '../../../playground/common/request-response-viewer';
import {
  ChartSeriesConfig,
  CurrencyHeader,
  EvolutionChart,
  KpiCard,
  KpiGrid,
  ProjectionColumn,
  ProjectionTable,
  SimulationWarnings,
  buildEvolutionSeries,
} from '../../../playground/endpoints/_shared';
import { LifeMaxMonthlyRow, LifeMaxResponse, LifeMaxYearlyRow } from './service';

const YEARLY_COLUMNS: ProjectionColumn<LifeMaxYearlyRow>[] = [
  { id: 'year', label: 'Año', isMoney: false },
  { id: 'age', label: 'Edad', isMoney: false },
  { id: 'actuarial_age', label: 'Edad actuarial', isMoney: false },
  { id: 'accumulated_prime', label: 'Prima acumulada', isMoney: true },
  { id: 'coverage_costs', label: 'Costo coberturas', isMoney: true },
  { id: 'policy_expenses', label: 'Gastos póliza', isMoney: true },
  { id: 'rentability', label: 'Rentado', isMoney: true },
  { id: 'policy_value', label: 'Valor póliza', isMoney: true },
  { id: 'withdrawable_amount', label: 'Monto rescatable', isMoney: true },
  { id: 'death_compensation', label: 'Indemn. fallecimiento', isMoney: true },
];

// `ProjectionTable` needs a plain numeric-index row; `coverage_costs_breakdown`
// still rides along untouched for the shared table's coverage-costs popover.
interface LifeMaxMonthlyTableRow {
  [key: string]: number | undefined;
}

function toMonthlyTableRow(row: LifeMaxMonthlyRow): LifeMaxMonthlyTableRow {
  return row as unknown as LifeMaxMonthlyTableRow;
}

// Emits `calendar_month`, so the shared `month` column can read it directly
// -- unlike Family Savings, which had to republish the index under its own
// key.
const MONTHLY_COLUMNS: ProjectionColumn<LifeMaxMonthlyTableRow>[] = [
  { id: 'month', label: 'Mes', isMoney: false },
  ...(YEARLY_COLUMNS.filter((column) => column.id !== 'year') as unknown as
  ProjectionColumn<LifeMaxMonthlyTableRow>[]),
];

const CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
  {
    dataKey: 'death_compensation',
    name: 'Indemn. fallecimiento',
    stroke: 'var(--bs-success)',
    strokeDasharray: '5 4',
  },
];

const MONTHLY_CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
];

interface LifeMaxResultViewProps {
  request: unknown;
  response?: LifeMaxResponse;
  error?: unknown;
}

export function LifeMaxResultView({
  request,
  response,
  error,
}: LifeMaxResultViewProps) {
  const [currency, setCurrency] = useState<Currency>('UF');
  const hasSuccess = !!response && !error;

  const monthlyRows = useMemo(
    () => (response?.projection_by_month ?? []).map(toMonthlyTableRow),
    [response],
  );

  return (
    <div className="mt-4">
      {hasSuccess && response && (
        <>
          <CurrencyHeader
            ufValue={response.uf_value}
            currency={currency}
            onCurrencyChange={setCurrency}
          />

          <SimulationWarnings warnings={response.warnings} />

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Prima única"
                value={
                  formatMoney(response.initial_contribution.total, currency, response.uf_value)
                }
                accent="text-primary"
              />
              <KpiCard
                label="Prima básica"
                value={
                  formatMoney(response.initial_contribution.basic, currency, response.uf_value)
                }
              />
              <KpiCard
                label="Prima excedente"
                value={
                  formatMoney(response.initial_contribution.excess, currency, response.uf_value)
                }
              />
              <KpiCard
                label="Ahorro bruto"
                value={formatMoney(response.savings.gross, currency, response.uf_value)}
                hint={`${response.timeframe_months} meses`}
                accent="text-success"
              />
              <KpiCard
                label="Interés ganado"
                value={formatMoney(response.savings.interest, currency, response.uf_value)}
              />
              <KpiCard
                label="Capital aportado neto"
                value={formatMoney(response.savings.net, currency, response.uf_value)}
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <EvolutionChart
              data={buildEvolutionSeries(
                response.projection_by_year,
                ['policy_value', 'death_compensation'],
                currency,
                response.uf_value,
              )}
              series={CHART_SERIES}
              currency={currency}
            />
          </div>

          {monthlyRows.length > 0 && (
            <>
              <div className="mb-4">
                <EvolutionChart
                  title="Evolución mensual de la póliza"
                  data={buildEvolutionSeries(
                    monthlyRows,
                    ['policy_value'],
                    currency,
                    response.uf_value,
                    ['month'],
                  )}
                  series={MONTHLY_CHART_SERIES}
                  currency={currency}
                  xKey="month"
                  xLabel="Mes"
                />
              </div>

              <div className="mb-4">
                <ProjectionTable
                  rows={monthlyRows}
                  columns={MONTHLY_COLUMNS}
                  currency={currency}
                  ufValue={response.uf_value}
                  title="Detalle mensual"
                  rowKey="month"
                />
              </div>
            </>
          )}

          <div className="mb-4">
            <ProjectionTable
              rows={response.projection_by_year}
              columns={YEARLY_COLUMNS}
              currency={currency}
              ufValue={response.uf_value}
            />
          </div>
        </>
      )}

      <RequestResponseViewer request={request} response={response} error={error} />
    </div>
  );
}
